/**
 * The Financial tab's sub-tabs, in the order they are shown. The open one
 * lives in the URL under `fin` (absent = summary), so a link opens it.
 */
export const FINANCIAL_VIEWS = [
  'summary',
  'channels',
  'partners',
  'transactions',
  'settlement',
  'issues',
] as const;

export type FinancialView = (typeof FINANCIAL_VIEWS)[number];

/**
 * The Partner's Financial tab (reports.partner.read without reports.financial.read):
 * the totals of its share, and the transactions without cost.
 */
export const PARTNER_FINANCIAL_VIEWS: readonly FinancialView[] = ['summary', 'transactions'];

export const isPartnerFinancialView = (view: FinancialView) =>
  PARTNER_FINANCIAL_VIEWS.includes(view);

export const FINANCIAL_VIEW_PARAM = 'fin';

export const FINANCIAL_VIEW_LABEL: Record<FinancialView, string> = {
  summary: 'Summary',
  channels: 'Payment channels by day',
  partners: 'Partner share',
  transactions: 'Transactions',
  settlement: 'Settlement',
  issues: 'Issues',
};

/**
 * The sub-tab in the URL. Anything unknown, or a sub-tab the user cannot see,
 * falls back to the summary.
 */
export function readFinancialView(
  raw: string | null,
  canSee: (view: FinancialView) => boolean = () => true,
): FinancialView {
  const view = FINANCIAL_VIEWS.find((candidate) => candidate === raw);
  return view && canSee(view) ? view : 'summary';
}

/** The sub-tabs drawn from the main financial report; the others fetch their own report. */
export const usesFinancialReport = (view: FinancialView) =>
  view === 'summary' || view === 'settlement' || view === 'issues';
